/**
 * Authentication state.
 *
 * On mount it tries a silent refresh: the HttpOnly cookie survives a reload
 * even though the access token (memory only) does not, so a page refresh
 * restores the session without ever putting a token in localStorage.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, aoPerderSessao, definirToken, ErroApi, type RespostaLogin, type Usuario } from '../api/cliente'

interface EstadoAuth {
  usuario: Usuario | null
  carregando: boolean
  /** True when the session may only enrol MFA. */
  precisaCadastrarMfa: boolean
  entrar: (email: string, senha: string, codigoMfa?: string) => Promise<RespostaLogin>
  sair: () => Promise<void>
  recarregar: () => Promise<void>
}

const Contexto = createContext<EstadoAuth | null>(null)

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [precisaCadastrarMfa, setPrecisaCadastrarMfa] = useState(false)

  const limpar = useCallback(() => {
    definirToken(null)
    setUsuario(null)
    setPrecisaCadastrarMfa(false)
  }, [])

  useEffect(() => {
    aoPerderSessao(limpar)
  }, [limpar])

  // Restaura a sessão a partir do cookie HttpOnly.
  useEffect(() => {
    let cancelado = false

    void (async () => {
      const renovou = await api.renovarSessao()
      if (cancelado) return

      if (renovou) {
        try {
          const eu = await api.get<Usuario>('/api/v1/auth/eu')
          if (!cancelado) {
            setUsuario(eu)
            setPrecisaCadastrarMfa(Boolean(eu.mfaObrigatorio) && !eu.mfaAtivo)
          }
        } catch {
          if (!cancelado) limpar()
        }
      }
      if (!cancelado) setCarregando(false)
    })()

    return () => {
      cancelado = true
    }
  }, [limpar])

  const entrar = useCallback(async (email: string, senha: string, codigoMfa?: string) => {
    const r = await api.post<RespostaLogin>('/api/v1/auth/login', { email, senha, codigoMfa })
    definirToken(r.accessToken)
    setUsuario(r.usuario)
    setPrecisaCadastrarMfa(r.precisaCadastrarMfa)
    return r
  }, [])

  const sair = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/sair')
    } catch {
      // Falha ao avisar o servidor não pode impedir o logout local.
    }
    limpar()
  }, [limpar])

  const recarregar = useCallback(async () => {
    try {
      const eu = await api.get<Usuario>('/api/v1/auth/eu')
      setUsuario(eu)
      setPrecisaCadastrarMfa(Boolean(eu.mfaObrigatorio) && !eu.mfaAtivo)
    } catch (erro) {
      if (erro instanceof ErroApi && erro.status === 401) limpar()
    }
  }, [limpar])

  const valor = useMemo<EstadoAuth>(
    () => ({ usuario, carregando, precisaCadastrarMfa, entrar, sair, recarregar }),
    [usuario, carregando, precisaCadastrarMfa, entrar, sair, recarregar],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useAuth(): EstadoAuth {
  const ctx = useContext(Contexto)
  if (!ctx) throw new Error('useAuth precisa estar dentro de <ProvedorAuth>.')
  return ctx
}

/** Human-readable role names, used across the UI. */
export const NOMES_PAPEL: Record<string, string> = {
  admin: 'Administrador da plataforma',
  admin_master_egba: 'Administrador master EGBA',
  egba: 'Equipe EGBA — Atendimento e publicações',
  parceiro: 'Parceiro',
  cliente: 'Cliente',
  operador: 'Operador',
  leitura: 'Consulta e auditoria',
}

/**
 * Papéis que trabalham a operação comercial — veem mercado, campanhas,
 * landing pages, empresas. Inclui a equipe `egba`: é ela quem fala com as
 * escolas; o que ela NÃO vê é a gestão do sistema (abaixo).
 */
export function podeAdministrar(papel: string | undefined): boolean {
  return papel === 'admin' || papel === 'admin_master_egba' || papel === 'egba'
}

/**
 * Papéis que mexem na máquina: configurações, auditoria, acessos, categorias
 * e tabelas de preço. A equipe operacional não vê estes itens — não por
 * desconfiança, mas porque tela de parâmetro técnico no menu de quem atende
 * escola é ruído que confunde.
 */
export function podeGerirSistema(papel: string | undefined): boolean {
  return papel === 'admin' || papel === 'admin_master_egba'
}

/**
 * O usuário enxerga esta vertical de negócio? Sem a lista gravada (contas
 * antigas, papel técnico), a resposta é sim — o recorte é opt-in.
 */
export function temVertical(
  usuario: { verticais?: Array<'escolas' | 'companhias'> } | null | undefined,
  vertical: 'escolas' | 'companhias',
): boolean {
  const lista = usuario?.verticais
  if (!Array.isArray(lista) || lista.length === 0) return true
  return lista.includes(vertical)
}
